import type {FC} from 'react'
import {useState, useEffect} from 'react'

import {Modal, ModalWithClose} from '@pleo-io/telescope'

import type {PlanResponse} from '~/api/deimos/billing'
import {useSubscription, usePlans} from '~/api/deimos/billing'
import {useDeimosStorageWithCompanyId} from '~/api/deimos/storage'
import {PlanType, PlanUpgradeSource} from '~/api/types/plan'
import {dayjs} from '~/dates/dayjs'
import {reportError} from '~/error/report'
import {useFlags} from '~/flags'
import {EmployeeStatus} from '~/shell/employee-status'
import {getEmployeeStatus, useUser, useUserMutations} from '~/shell/user'

import {ChoosePlanContainer} from './manage-plan/choose-plan-container'
import ModalConfirmPlan from './modal-confirm-plan'
import ModalManagePlan from './modal-manage-plan'

const DAYS_BEFORE_END_OF_TRIAL = 5

/*
    Prevents calling subscription API unnecessarily if conditions are not met
*/
function useEndTrialModalRequirements() {
    const user = useUser()
    const hasSeenEndTrial = Boolean(getEmployeeStatus(user)[EmployeeStatus.hasSeenEndTrialModal])
    const isCustomerOnContract = useSubscription(user?.companyId).data?.contract

    const {
        result: {data: pricingPlanData},
    } = useDeimosStorageWithCompanyId('pricing_plan', user?.companyId || '')

    const isFreemiumUser =
        !pricingPlanData?.value ||
        pricingPlanData?.value === 'free' ||
        pricingPlanData?.value === 'starter'

    const userExists = Boolean(user)

    const shouldShowEndTrialModal =
        isFreemiumUser && userExists && !hasSeenEndTrial && !isCustomerOnContract

    return {
        shouldShowEndTrialModal,
    }
}

function useEndTrial() {
    const user = useUser()

    const {data: subscription} = useSubscription(user?.companyId)
    const {data: plans} = usePlans()

    const [isEndTrialModalOpen, setIsEndTrialModalOpen] = useState(false)

    const {setEmployeeStateKey} = useUserMutations()

    const dismissEndTrialModal = () => {
        setEmployeeStateKey(EmployeeStatus.hasSeenEndTrialModal, true)
        setIsEndTrialModalOpen(false)
    }

    const hasSubscription = subscription !== undefined
    const userExists = Boolean(user)
    const isTeamWithCommitment = Boolean(
        subscription?.planSubscriptionItem.plan.type &&
            [PlanType.TEAM].includes(subscription?.planSubscriptionItem.plan.type) &&
            subscription?.planSubscriptionItem.plan.userCommitment !== 0,
    )
    const isFreeProOrTeam = Boolean(
        subscription?.planSubscriptionItem.plan.type &&
            [PlanType.FREE, PlanType.PRO, PlanType.TEAM].includes(
                subscription?.planSubscriptionItem.plan.type,
            ),
    )

    useEffect(() => {
        const getIsEligibleForDowngrade = () => {
            const planObj = plans?.find(({type}: PlanResponse) => type === PlanType.FREE)
            return Boolean(planObj?.eligibility)
        }

        const getIsTrialClose = () => {
            if (subscription?.isInTrial && subscription?.trial?.end) {
                // In this case future date is DAYS_BEFORE_END_OF_TRIAL days in the future
                const futureDate =
                    Number(new Date()) + 1000 * 60 * 60 * 24 * DAYS_BEFORE_END_OF_TRIAL
                // Is the trial end date closer in the future than DAYS_BEFORE_END_OF_TRIAL days?
                const enoughTimePassedOnTrial =
                    Number(new Date(subscription.trial.end)) < futureDate
                return enoughTimePassedOnTrial
            }
            // no sub, not on trial, or null trial object
            return false
        }

        if (
            getIsTrialClose() &&
            userExists &&
            hasSubscription &&
            isFreeProOrTeam &&
            !isTeamWithCommitment &&
            getIsEligibleForDowngrade()
        ) {
            setIsEndTrialModalOpen(true)
        }
    }, [
        hasSubscription,
        userExists,
        isFreeProOrTeam,
        isTeamWithCommitment,
        subscription?.isInTrial,
        subscription?.trial?.end,
        plans,
    ])

    return {
        isEndTrialModalOpen,
        dismissEndTrialModal,
    }
}

const EndTrialModal: FC = () => {
    const user = useUser()

    const {isEndTrialModalOpen, dismissEndTrialModal} = useEndTrial()
    const [showModalConfirm, setShowModalConfirm] = useState(false)

    const {
        data: subscription,
        mutations: {updateSubscription},
    } = useSubscription(user?.companyId)

    const updatePlan = async (planSelected: PlanType, close: Function, setLoading: Function) => {
        // Team plan by default selects a tier 0 plan which is intervalCommitment 1 and userCommitment 0
        try {
            await updateSubscription({
                companyId: user?.companyId,
                planType: planSelected,
                startDate: dayjs(subscription?.trial?.end || subscription?.started),
                intervalCommitment: planSelected === PlanType.TEAM ? 1 : undefined,
                userCommitment: planSelected === PlanType.TEAM ? 0 : undefined,
                planUpgradeSource: PlanUpgradeSource.TRIAL_BANNER,
            })
            close()
        } catch (err) {
            reportError(err)
        } finally {
            setLoading(false)
            dismissEndTrialModal()
        }
    }

    return (
        <>
            <Modal isOpen={isEndTrialModalOpen}>
                <ModalManagePlan
                    trialEndDate={
                        subscription?.trial ? new Date(subscription.trial.end) : new Date()
                    }
                    isInTrial={Boolean(subscription?.isInTrial)}
                    currentPlan={subscription?.planSubscriptionItem?.plan?.type ?? PlanType.TEAM}
                    updatePlan={updatePlan}
                    onDismiss={() => dismissEndTrialModal()}
                    isSubscriptionEmpty={subscription === undefined}
                    email={subscription?.billingEmail ?? ''}
                    isDismissible={false}
                    isFreemiumConfirm={true}
                    showModalConfirmCallback={() => setShowModalConfirm(true)}
                />
            </Modal>
            <ModalWithClose isOpen={showModalConfirm} onDismiss={() => setShowModalConfirm(false)}>
                <ModalConfirmPlan onDismiss={() => setShowModalConfirm(false)} />
            </ModalWithClose>
        </>
    )
}

const ChoosePlan: FC = () => {
    const {isEndTrialModalOpen, dismissEndTrialModal} = useEndTrial()

    return (
        <ChoosePlanContainer
            isOpen={isEndTrialModalOpen}
            onDismiss={dismissEndTrialModal}
            trackingOrigin="end-trial-modal"
        />
    )
}

const EndTrialModalRequirements: FC = () => {
    // Prevents subscription from being called unnecessarily
    const {shouldShowEndTrialModal} = useEndTrialModalRequirements()

    // LD use flag for choose plan modal
    const {choosePlanModal} = useFlags()

    if (!shouldShowEndTrialModal) {
        return null
    }

    return choosePlanModal ? <ChoosePlan /> : <EndTrialModal />
}

export {EndTrialModalRequirements as EndTrialModal, useEndTrial, useEndTrialModalRequirements}
